<template>
  <div class="find-user">
    <h2 class="mb-4">Users</h2>
    <div class="filter-container pa-4">
      <div class="d-flex">
        <v-text-field
          v-model="filterValue"
          label="Search"
          class="mr-3"
          @keyup.enter="applyFilter"
          type="text"
          clearable
          append-icon="mdi-magnify"
          @click:append="applyFilter"
        ></v-text-field>
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          label="Type"
          class="mr-3 type-select"
          @change="onTypeChange"
        ></v-select>
        <v-select
          v-model="filterField"
          :items="searchFields"
          label="Field"
          class="filter-field-select"
        ></v-select>
      </div>
      <div class="d-flex justify-end mt-2">
        <v-btn small color="primary" class="mr-2" @click="applyFilter">
          <v-icon left>mdi-magnify</v-icon>Search
        </v-btn>
        <v-btn small color="error" :disabled="!isFilterApplied" @click="clearFilter">
          <v-icon left>mdi-close</v-icon>Clear
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="results"
      :server-items-length="totalItems"
      :options.sync="paginationOptions"
      :loading="loading"
      :footer-props="{
        'items-per-page-options': [5, 10, 15, 20, 25, 50, 100, 200],
        'items-per-page-text': 'Rows per page:',
        'show-current-page': true
      }"
      @update:options="handlePaginationChange"
    >
      <template v-slot:item.action="{ item }">
        <div class="actions">
          <v-icon class="mr-2" @click="navigateTo(item)">search</v-icon>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: 'FindUser',
  data() {
    return {
      selectedType: 'users',
      typeOptions: [
        { text: 'Users', value: 'users' },
        { text: 'End Customers', value: 'endCustomers' },
      ],
      filterValue: '',
      filterField: 'email',
      searchFields: [
        { text: 'Email', value: 'email' },
      ],
      appliedFilter: { field: '', value: '' },
      results: [],
      totalItems: 0,
      loading: false,
      customerCache: {},
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      currentPageNumber: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    isFilterApplied() {
      return this.appliedFilter.value !== '';
    },
    headers() {
      if (this.selectedType === 'users') {
        return [
          { text: 'Email', value: 'email', sortable: false },
          { text: 'Type', value: 'type', sortable: false },
          { text: 'Customer', value: 'customerName', sortable: false },
          {
            text: '', value: 'action', sortable: false, width: '60px',
          },
        ];
      }
      return [
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Customer', value: 'customerName', sortable: false },
        {
          text: '', value: 'action', sortable: false, width: '60px',
        },
      ];
    },
    endpoint() {
      return this.selectedType === 'users' ? 'api/users' : 'api/end_customers';
    },
  },
  methods: {
    async getObjects() {
      this.loading = true;
      let filterParams = '';
      if (this.appliedFilter.value !== '') {
        filterParams = `&${this.appliedFilter.field}=${encodeURIComponent(this.appliedFilter.value)}`;
      }
      try {
        const { data } = await this.$http.get(
          `/${this.endpoint}?page=${this.currentPageNumber}&itemsPerPage=${this.itemsPerPage}${filterParams}`,
          { headers: { Accept: 'application/ld+json' } },
        );
        this.totalItems = data['hydra:totalItems'] || 0;
        const members = data['hydra:member'] || [];
        if (this.selectedType === 'users') {
          await this.mapUsers(members);
        } else {
          await this.mapEndCustomers(members);
        }
      } catch (error) {
        this.$store.commit('setPopupAlert', {
          title: 'Search Error',
          message: error.response?.data?.detail || 'Failed to load data.',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async mapUsers(members) {
      const rows = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const user of members) {
        const custId = this.extractCustomerId(user.customer);
        // eslint-disable-next-line no-await-in-loop
        const custName = custId ? await this.resolveCustomerName(custId) : '—';
        rows.push({
          id: user.id,
          email: user.email,
          type: this.getRoleLabel(user.roles),
          customerName: custName,
          customerId: custId,
          roles: user.roles,
          source: 'user',
        });
      }
      this.results = rows;
    },
    async mapEndCustomers(members) {
      const rows = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const ec of members) {
        const memberships = ec.customerMemberships || [];
        if (memberships.length === 0) {
          rows.push({
            id: ec.id,
            email: ec.email,
            customerName: '—',
            customerId: null,
            source: 'endCustomer',
          });
        } else {
          // eslint-disable-next-line no-restricted-syntax
          for (const m of memberships) {
            const custId = this.extractCustomerId(m.customer);
            // eslint-disable-next-line no-await-in-loop
            const custName = custId ? await this.resolveCustomerName(custId) : '—';
            rows.push({
              id: ec.id,
              email: ec.email,
              customerName: custName,
              customerId: custId,
              source: 'endCustomer',
            });
          }
        }
      }
      this.results = rows;
    },
    handlePaginationChange(options) {
      this.currentPageNumber = options.page;
      this.itemsPerPage = options.itemsPerPage;
      this.getObjects();
    },
    applyFilter() {
      if (!this.filterField) return;
      this.appliedFilter = {
        field: this.filterField,
        value: this.filterValue || '',
      };
      this.currentPageNumber = 1;
      this.paginationOptions.page = 1;
      this.getObjects();
    },
    clearFilter() {
      this.filterValue = '';
      this.appliedFilter = { field: '', value: '' };
      this.paginationOptions.sortBy = [];
      this.paginationOptions.sortDesc = [];
      this.currentPageNumber = 1;
      this.paginationOptions.page = 1;
      this.getObjects();
    },
    onTypeChange() {
      this.results = [];
      this.totalItems = 0;
      this.currentPageNumber = 1;
      this.paginationOptions.page = 1;
      this.getObjects();
    },
    getRoleLabel(roles) {
      if (!roles || roles.length === 0) return 'Unknown';
      if (roles.includes('ROLE_SUPER_ADMIN')) return 'Superuser';
      if (roles.includes('ROLE_SERVICE_INTEGRATION')) return 'Service Integration';
      if (roles.includes('ROLE_ADMIN')) return 'Admin';
      if (roles.includes('ROLE_INTEGRATION')) return 'Integration';
      if (roles.includes('ROLE_USER')) return 'User';
      return roles[0];
    },
    extractCustomerId(customerIri) {
      if (!customerIri) return null;
      const match = String(customerIri).match(/\/api\/customers\/(\d+)/);
      return match ? match[1] : String(customerIri);
    },
    async resolveCustomerName(customerId) {
      if (this.customerCache[customerId]) return this.customerCache[customerId];
      try {
        const resp = await this.$http.get(`/api/customers/${customerId}`);
        const name = resp.data.name || `Customer #${customerId}`;
        this.customerCache[customerId] = name;
        return name;
      } catch {
        return `Customer #${customerId}`;
      }
    },
    navigateTo(item) {
      this.$store.commit('setBackRoute', '/find-user');
      if (item.source === 'endCustomer') {
        if (item.customerId) {
          this.$router.push({ name: 'customerdetailsendcustomer', params: { id: item.customerId } });
        }
        return;
      }
      const roles = item.roles || [];
      if (roles.includes('ROLE_SUPER_ADMIN')) {
        this.$router.push({ name: 'superusers' });
        return;
      }
      if (roles.includes('ROLE_SERVICE_INTEGRATION')) {
        this.$router.push({ name: 'serviceintegrationusers' });
        return;
      }
      if (!item.customerId) return;
      if (roles.includes('ROLE_ADMIN')) {
        this.$router.push({ name: 'customerdetailsadmins', params: { id: item.customerId } });
        return;
      }
      if (roles.includes('ROLE_INTEGRATION')) {
        this.$router.push({ name: 'customerdetailsintegration', params: { id: item.customerId } });
        return;
      }
      this.$router.push({ name: 'customerdetailsusers', params: { id: item.customerId } });
    },
  },
  async created() {
    await this.getObjects();
  },
};
</script>

<style scoped>
.find-user {
  max-width: 900px;
  margin: 0 auto;
}

.filter-container {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fafafa;
}

.type-select {
  max-width: 200px;
}

.filter-field-select {
  max-width: 150px;
}
</style>
